import { useState, useCallback, useMemo } from 'react';
import type { Layer, Step, StepId } from './types';

// ─── Les 5 étapes du parcours métier ─────────────────────────────────────────

export const STEPS: Step[] = [
  { id: 'consumption', number: 1, title: 'Identifier la consommation', subtitle: 'Prospects & gros consommateurs' },
  { id: 'resources',   number: 2, title: 'Ressources énergétiques',    subtitle: 'ENR & récupération de chaleur' },
  { id: 'routing',     number: 3, title: 'Étude de tracé du réseau',   subtitle: 'Contraintes & infrastructures' },
  { id: 'future',      number: 4, title: 'Développement futur',        subtitle: 'Projets & zones d\'aménagement' },
  { id: 'context',     number: 5, title: 'Présentations & usages annexes', subtitle: 'Contours administratifs, cadastre' },
];

// ─── Catalogue de couches (une couche peut appartenir à plusieurs étapes) ────

export const CATALOG: Layer[] = [
  // 1. Consommation
  { id: 'pdl-grdf',        label: 'PDL GRDF',                       source: 'engie', steps: ['consumption'], hint: 'Points de livraison gaz' },
  { id: 'pdl-enedis',      label: 'PDL Enedis',                     source: 'engie', steps: ['consumption'], hint: 'Points de livraison électricité' },
  { id: 'pdl-rcf',         label: 'PDL Réseaux de Chaleur / Froid', source: 'engie', steps: ['consumption'] },
  { id: 'buildings-conso', label: 'Bâtiments — Conso. chaud',       source: 'engie', steps: ['consumption'] },
  { id: 'postes-gaz',      label: 'Postes de livraison gaz',        source: 'engie', steps: ['consumption', 'routing'] },
  { id: 'postes-enedis',   label: 'Postes sources ENEDIS',          source: 'open',  steps: ['consumption', 'routing'] },
  { id: 'canal-engie',     label: 'Canalisations ENGIE existantes', source: 'engie', steps: ['consumption', 'routing'] },
  { id: 'canal-non-engie', label: 'Canalisations non ENGIE',        source: 'open',  steps: ['consumption', 'routing'] },
  // 2. Ressources
  { id: 'step',            label: 'Stations d\'épuration (STEP)',   source: 'open',  steps: ['resources'] },
  { id: 'uve',             label: 'UVE — valorisation énergétique', source: 'open',  steps: ['resources'] },
  { id: 'datacenters',     label: 'Datacenters',                    source: 'open',  steps: ['resources'] },
  { id: 'entrepots-frigo', label: 'Entrepôts frigorifiques',        source: 'open',  steps: ['resources'] },
  { id: 'centrales',       label: 'Centrales ENGIE',                source: 'engie', steps: ['resources', 'consumption'] },
  { id: 'geothermie',      label: 'Géothermies sur sonde',          source: 'open',  steps: ['resources'] },
  { id: 'icpe',            label: 'ICPE',                           source: 'open',  steps: ['resources'] },
  { id: 'dry-coolers',     label: 'Dry coolers / groupes froids',   source: 'open',  steps: ['resources'] },
  // 3. Tracé
  { id: 'reseau-grdf',     label: 'Réseau GRDF',                    source: 'engie', steps: ['routing'] },
  { id: 'reseau-enedis',   label: 'Réseau ENEDIS',                  source: 'open',  steps: ['routing'] },
  { id: 'routes',          label: 'Routes',                         source: 'open',  steps: ['routing'] },
  { id: 'zones-archeo',    label: 'Zones archéologiques',           source: 'open',  steps: ['routing'] },
  { id: 'rivieres',        label: 'Rivières & hydrographie',        source: 'open',  steps: ['routing'] },
  // 4. Futur
  { id: 'projets-immo',    label: 'Projets immobiliers',            source: 'engie', steps: ['future'] },
  { id: 'zac',             label: 'ZAC — zones d\'aménagement',     source: 'open',  steps: ['future'] },
  { id: 'reseaux-const',   label: 'Réseaux en construction',        source: 'engie', steps: ['future'] },
  { id: 'zones-reseaux',   label: 'Zones de réseaux en construction', source: 'engie', steps: ['future'] },
  // 5. Contexte
  { id: 'contours-admin',  label: 'Contours administratifs',        source: 'open',  steps: ['context'] },
  { id: 'perimetres-contrat', label: 'Périmètre contrat (DSP)',      source: 'engie', steps: ['context'] },
  { id: 'zones-classement',label: 'Zones de classement RCU/RFU',    source: 'open',  steps: ['context'] },
  { id: 'parcelles',       label: 'Parcelles cadastrales',          source: 'open',  steps: ['context'] },
  { id: 'quartiers-prio',  label: 'Quartiers prioritaires',         source: 'open',  steps: ['context'] },
  { id: 'zones-clim',      label: 'Zones climatiques',              source: 'open',  steps: ['context'] },
  { id: 'stations-meteo',  label: 'Stations météo',                 source: 'open',  steps: ['context'] },
  { id: 'servitudes',      label: 'Servitudes aéronautiques',       source: 'open',  steps: ['context'] },
];

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useJourneyLayers() {
  const [visible, setVisible] = useState<Set<string>>(new Set());
  const [openStep, setOpenStep] = useState<StepId | null>('consumption');
  const [search, setSearch] = useState('');

  const toggleVisible = useCallback((id: string) => {
    setVisible((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const toggleStep = useCallback((id: StepId) => {
    setOpenStep((p) => (p === id ? null : id));
  }, []);

  const layersByStep = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (l: Layer) => !q || l.label.toLowerCase().includes(q);
    const map = new Map<StepId, Layer[]>();
    for (const step of STEPS) {
      map.set(step.id, CATALOG.filter((l) => l.steps.includes(step.id) && match(l)));
    }
    return map;
  }, [search]);

  return {
    steps: STEPS,
    layersByStep,
    visible,
    openStep,
    search,
    activeCount: visible.size,
    setSearch,
    toggleVisible,
    toggleStep,
    clearAll: () => setVisible(new Set()),
  };
}
